import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { cn } from "./cn.js";
import type { ChatCompanyResearch } from "./messages.js";
import { fetchCompanyPack } from "./onboarding-api.js";
import { SiteFavicon, hostFromUrl } from "./SiteFavicon.js";

type Props = {
	rows: ChatCompanyResearch[];
	onPatch?: (packId: string, patch: Partial<ChatCompanyResearch>) => void;
	onConfirm?: (packId: string) => void;
	confirmingId?: string | null;
};

/** First pass — finishes in ~15s. The real Firecrawl + pack work is still going. */
const OPENING_BEATS = [
	"Opening the site",
	"Reading the homepage",
	"Learning what you sell",
	"Hearing how you sound",
	"Seeing how you show up",
	"Figuring out how we can help",
] as const;

/**
 * Long-tail log after the checklist. The workflow sits on one “research” step
 * for minutes; these keep the card moving the way Claude/OpenAI deep research
 * keeps a thought stream going.
 */
const DEEP_TRAIL = [
	"Checking about, pricing, and product pages",
	"Reading how they describe the offer",
	"Listening for voice — formal, warm, or salesy",
	"Looking at who this is really for",
	"Scanning public mentions and social",
	"Seeing what the site asks people to do",
	"Noting gaps a marketing team would feel",
	"Matching the brand to work we can actually do",
	"Deciding if a working copy of the site would help",
	"Cross-checking the homepage against inner pages",
	"Tightening what we’d do first vs later",
	"Writing the pack in plain language",
] as const;

/** After the unique trail, keep a live line so the last beat never freezes. */
const DEEP_HOLD = [
	"Second pass on the offer",
	"Checking the notes against the homepage",
	"Looking for a sharper one-liner",
	"Making sure we don’t invent facts",
	"Seeing if marketing help is in reach",
	"Putting the last pieces of the pack together",
] as const;

const OPENING_MS = 2400;
const TRAIL_MS = 3200;
const HOLD_MS = 3800;

function hostnameLabel(url: string): string {
	return hostFromUrl(url);
}

function formatElapsed(ms: number): string {
	const total = Math.max(0, Math.floor(ms / 1000));
	const minutes = Math.floor(total / 60);
	const seconds = total % 60;
	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function openingShown(elapsedMs: number, progress: number, done: boolean): number {
	if (done) return OPENING_BEATS.length;
	const byTime = Math.min(
		OPENING_BEATS.length,
		1 + Math.floor(elapsedMs / OPENING_MS),
	);
	const byProgress = Math.min(
		OPENING_BEATS.length,
		1 + Math.floor((Math.max(0, progress) / 100) * (OPENING_BEATS.length - 1)),
	);
	return Math.max(byTime, byProgress);
}

function trailCount(elapsedMs: number, openingComplete: boolean): number {
	if (!openingComplete) return 0;
	const openingDur = (OPENING_BEATS.length - 1) * OPENING_MS;
	const deepMs = Math.max(0, elapsedMs - openingDur);
	const unique = Math.min(
		DEEP_TRAIL.length,
		1 + Math.floor(deepMs / TRAIL_MS),
	);
	if (unique < DEEP_TRAIL.length) return unique;
	const holdMs = deepMs - (DEEP_TRAIL.length - 1) * TRAIL_MS;
	return DEEP_TRAIL.length + Math.floor(holdMs / HOLD_MS);
}

function trailLineAt(index: number): string {
	if (index < DEEP_TRAIL.length) return DEEP_TRAIL[index] ?? DEEP_TRAIL[0];
	const holdIndex = (index - DEEP_TRAIL.length) % DEEP_HOLD.length;
	return DEEP_HOLD[holdIndex] ?? DEEP_HOLD[0];
}

/** Region of the wireframe the scan highlight sits on. */
type ScanRegion =
	| "page"
	| "pages"
	| "hero"
	| "cta"
	| "offers"
	| "voice"
	| "presence";

const SCAN_LABEL: Record<ScanRegion, string> = {
	page: "Scanning",
	pages: "Inner pages",
	hero: "First impression",
	cta: "Call to action",
	offers: "What you sell",
	voice: "Tone of voice",
	presence: "How you show up",
};

/** Parallel to OPENING_BEATS — where the highlight sits during each beat. */
const OPENING_REGIONS: readonly ScanRegion[] = [
	"page",
	"hero",
	"offers",
	"voice",
	"presence",
	"page",
];

/** Parallel to DEEP_TRAIL. */
const TRAIL_REGIONS: readonly ScanRegion[] = [
	"pages",
	"offers",
	"voice",
	"voice",
	"presence",
	"cta",
	"page",
	"offers",
	"hero",
	"pages",
	"page",
	"page",
];

/** Parallel to DEEP_HOLD. */
const HOLD_REGIONS: readonly ScanRegion[] = [
	"offers",
	"hero",
	"voice",
	"page",
	"offers",
	"page",
];

function scanRegionFor(
	openingDone: boolean,
	shown: number,
	deepCount: number,
): ScanRegion {
	if (!openingDone) {
		return OPENING_REGIONS[Math.max(0, shown - 1)] ?? "page";
	}
	if (deepCount === 0) return "page";
	const index = deepCount - 1;
	if (index < DEEP_TRAIL.length) return TRAIL_REGIONS[index] ?? "page";
	return (
		HOLD_REGIONS[(index - DEEP_TRAIL.length) % HOLD_REGIONS.length] ?? "page"
	);
}

function screenshotUrl(siteUrl: string): string | null {
	try {
		const parsed = new URL(
			/^https?:\/\//i.test(siteUrl) ? siteUrl : `https://${siteUrl}`,
		);
		if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
			return null;
		}
		return `https://s.wordpress.com/mshots/v1/${encodeURIComponent(parsed.href)}?w=1024`;
	} catch {
		return null;
	}
}

/**
 * Live screenshot of the homepage via WordPress mShots (keyless). The service
 * returns a placeholder until the render is done, so poll by re-loading the
 * image until it comes back at real width. An iframe would be simpler but
 * most sites block framing with X-Frame-Options / frame-ancestors.
 */
function useHomepageShot(siteUrl: string): string | null {
	const [shot, setShot] = useState<string | null>(null);

	useEffect(() => {
		const url = screenshotUrl(siteUrl);
		if (!url) {
			setShot(null);
			return;
		}
		let cancelled = false;
		let timer = 0;
		let attempts = 0;

		const probe = () => {
			const img = new Image();
			img.referrerPolicy = "no-referrer";
			// Cache-bust so the browser can't keep serving a cached placeholder.
			const src = `${url}&_=${Date.now()}`;
			img.onload = () => {
				if (cancelled) return;
				// The "generating…" placeholder is usually a 400×300 gif, but
				// mShots sometimes renders its own loading page at full size —
				// so keep polling and let later (real) renders replace it.
				if (img.naturalWidth >= 900) setShot(src);
				schedule();
			};
			img.onerror = () => {
				if (!cancelled) schedule();
			};
			img.src = src;
		};

		const schedule = () => {
			attempts += 1;
			if (attempts > 20) return; // ~1.5 min, then keep whatever we have
			timer = window.setTimeout(probe, 4000);
		};

		setShot(null);
		probe();
		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [siteUrl]);

	return shot;
}

/**
 * A browser window "reading" the client's site: their favicon in the address
 * bar and a live screenshot of the homepage under a sweeping scan beam, with
 * a labeled highlight that glides to whichever region the current beat is
 * about. A wireframe stands in until the screenshot arrives.
 */
function ResearchScanPreview({
	siteUrl,
	region,
	progress,
}: {
	siteUrl: string;
	region: ScanRegion;
	progress: number;
}) {
	const host = hostnameLabel(siteUrl);
	const shot = useHomepageShot(siteUrl);
	return (
		<div className="mc-scan" aria-hidden="true">
			<div className="mc-scan-chrome">
				<span className="mc-scan-dots">
					<i />
					<i />
					<i />
				</span>
				<span className="mc-scan-address">
					<SiteFavicon url={siteUrl} className="mc-scan-favicon" />
					<span className="mc-scan-host">{host}</span>
				</span>
				<span className="mc-scan-reading">
					<span className="mc-scan-reading-dot" />
					reading
				</span>
			</div>
			<div className="mc-scan-page">
				{shot ? (
					<img
						className="mc-scan-shot"
						src={shot}
						alt=""
						referrerPolicy="no-referrer"
					/>
				) : (
					<>
						<div className="mc-scan-nav">
							<span className="mc-scan-brand" />
							<span className="mc-scan-menu">
								<i />
								<i />
								<i />
							</span>
						</div>
						<div className="mc-scan-hero">
							<div className="mc-scan-hero-copy">
								<span className="mc-scan-bar mc-scan-bar--xl" />
								<span className="mc-scan-bar mc-scan-bar--lg" />
								<span className="mc-scan-bar mc-scan-bar--sub" />
								<span className="mc-scan-cta" />
							</div>
							<div className="mc-scan-hero-art" />
						</div>
						<div className="mc-scan-cards">
							<i />
							<i />
							<i />
						</div>
						<div className="mc-scan-text">
							<span className="mc-scan-bar" />
							<span className="mc-scan-bar" />
							<span className="mc-scan-bar" />
						</div>
						<div className="mc-scan-footer">
							<i />
							<i />
							<i />
							<i />
						</div>
					</>
				)}
				<span className="mc-scan-bit" />
				<span className="mc-scan-bit" />
				<span className="mc-scan-bit" />
				<span className="mc-scan-bit" />
				<span className="mc-scan-bit" />
				<div className="mc-scan-beam" />
				<div
					className={`mc-scan-highlight mc-scan-highlight--${region}`}
					data-label={SCAN_LABEL[region]}
				/>
			</div>
			<div className="mc-scan-progress">
				<span
					style={{ width: `${Math.min(97, Math.max(6, progress))}%` }}
				/>
			</div>
		</div>
	);
}

export function CompanyPackCards({
	rows,
	onPatch,
	onConfirm,
	confirmingId,
}: Props) {
	if (rows.length === 0) return null;
	return (
		<div className="mc-pack-list" role="list" aria-label="Brand research">
			{rows.map((row) => (
				<CompanyPackCard
					key={row.packId}
					row={row}
					onPatch={onPatch}
					onConfirm={onConfirm}
					confirming={confirmingId === row.packId}
				/>
			))}
		</div>
	);
}

function CompanyPackCard({
	row,
	onPatch,
	onConfirm,
	confirming,
}: {
	row: ChatCompanyResearch;
	onPatch?: (packId: string, patch: Partial<ChatCompanyResearch>) => void;
	onConfirm?: (packId: string) => void;
	confirming: boolean;
}) {
	const [local, setLocal] = useState(row);
	const startedAt = useRef(Date.now());
	const [now, setNow] = useState(() => Date.now());
	const ready =
		local.status === "ready" ||
		local.status === "confirmed" ||
		local.phase === "ready";
	const failed = local.status === "failed" || local.phase === "failed";
	const confirmed = local.status === "confirmed";
	const card = local.card;
	const live = !ready && !failed;

	useEffect(() => {
		setLocal(row);
	}, [row]);

	useEffect(() => {
		if (!live) return;
		const id = window.setInterval(() => setNow(Date.now()), 400);
		return () => window.clearInterval(id);
	}, [live]);

	useEffect(() => {
		if (!live) return;
		let cancelled = false;
		const tick = async () => {
			const result = await fetchCompanyPack(local.packId);
			if (!result.ok || cancelled) return;
			const pack = result.pack;
			const patch: Partial<ChatCompanyResearch> = {
				status: pack.status,
				progress: pack.progress,
				phase:
					pack.status === "ready" || pack.status === "confirmed"
						? "ready"
						: pack.status === "failed"
							? "failed"
							: "progress",
				lastError: pack.lastError,
				card: pack.card,
				brandName: pack.card?.brandName ?? local.brandName,
				siteUrl: pack.siteUrl,
			};
			setLocal((prev) => ({ ...prev, ...patch }));
			onPatch?.(local.packId, patch);
		};
		void tick();
		const id = window.setInterval(() => void tick(), 2500);
		return () => {
			cancelled = true;
			window.clearInterval(id);
		};
	}, [live, local.packId, local.brandName, onPatch]);

	const host = hostnameLabel(local.siteUrl);
	const title = card?.brandName ?? local.brandName ?? host;
	const elapsed = now - startedAt.current;
	const shown = openingShown(elapsed, local.progress || 0, ready);
	const openingDone = shown >= OPENING_BEATS.length;
	const deepCount = trailCount(elapsed, openingDone && live);
	const scanRegion = scanRegionFor(openingDone, shown, deepCount);
	const currentLine: string = !openingDone
		? (OPENING_BEATS[Math.max(0, shown - 1)] ?? "Opening the site")
		: deepCount > 0
			? trailLineAt(deepCount - 1)
			: "Figuring out how we can help";

	return (
		<article
			className={cn(
				"mc-pack-card",
				live && "mc-pack-card--live",
				ready && "mc-pack-card--ready",
				failed && "mc-pack-card--failed",
				confirmed && "mc-pack-card--confirmed",
			)}
			role="listitem"
		>
			<header className="mc-pack-card-head">
				<SiteFavicon url={local.siteUrl} live={live} />
				<div className="mc-pack-card-meta">
					<p className="mc-pack-card-title">
						{live ? (
							<Shimmer as="span" duration={1.6} className="mc-pack-shimmer">
								{openingDone
									? `Thinking about ${host}`
									: `Learning ${host}`}
							</Shimmer>
						) : (
							title
						)}
					</p>
					<p className="mc-pack-card-url" title={local.siteUrl}>
						{host}
						{live ? (
							<span className="mc-pack-elapsed"> · {formatElapsed(elapsed)}</span>
						) : null}
					</p>
				</div>
			</header>

			{live ? (
				<div className="mc-pack-live">
					<ResearchScanPreview
						siteUrl={local.siteUrl}
						region={scanRegion}
						progress={local.progress || 0}
					/>
					<p className="mc-pack-now" aria-live="polite">
						<Shimmer as="span" duration={1.5} className="mc-pack-shimmer">
							{currentLine}
						</Shimmer>
					</p>
				</div>
			) : null}

			{failed ? (
				<p className="mc-pack-card-error">
					{local.lastError ??
						"I couldn’t get a clear read. Paste the URL again and I’ll retry."}
				</p>
			) : null}

			{ready && card ? (
				<div className="mc-pack-card-body">
					<p className="mc-pack-card-liner">{card.oneLiner}</p>
					{card.offers.length > 0 ? (
						<ul className="mc-pack-card-offers">
							{card.offers.slice(0, 4).map((offer) => (
								<li key={offer}>{offer}</li>
							))}
						</ul>
					) : null}
					<p className="mc-pack-card-tone">
						Sounds {card.tone.toLowerCase()}
						{card.audience ? ` · ${card.audience}` : ""}
					</p>
				</div>
			) : null}

			{ready && !confirmed && onConfirm ? (
				<div className="mc-pack-card-actions">
					<Button
						type="button"
						size="sm"
						className="mc-pack-card-cta"
						disabled={confirming}
						onClick={() => onConfirm(local.packId)}
					>
						{confirming ? "Saving…" : "Looks right"}
					</Button>
				</div>
			) : null}
		</article>
	);
}
